import BuildIcon from '@mui/icons-material/Build'
import CloseIcon from '@mui/icons-material/Close'
import { IconButton, Paper, Stack, Typography } from '@mui/material'
import React, { useContext, useEffect, useMemo, useRef } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { TwilioConversationsContext, WindowSizeContext } from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useGetUnreadNotificationsByModifiedEntityQuery,
  useMarkNotificationAsReadMutation
} from '../../redux/api/notificationApi'
import { ChatMessages } from './ChatMessages'
import { useChat } from './useChat'

export const RepairOrderChatDialog = ({
  repairOrderId,
  repairOrderNumber,
  dialogOpen,
  setDialogOpen,
  dviDisplay
}: {
  repairOrderId: string
  repairOrderNumber: number
  dialogOpen: boolean
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  dviDisplay?: boolean
}) => {
  const { overTabletBreakpoint } = useContext(AppContext)
  const { accessToken, userId } = useAccessTokenAndShopId()
  const { height } = useContext(WindowSizeContext)
  const { connectionStatus } = useContext(TwilioConversationsContext)

  const [markAsRead] = useMarkNotificationAsReadMutation()
  const { data: unreadNotifications } =
    useGetUnreadNotificationsByModifiedEntityQuery({
      authorization: accessToken,
      modifiedEntity: [repairOrderId]
    })
  const unreadNotificationIds = useMemo(
    () => (unreadNotifications || []).map((notification) => notification.id!),
    [unreadNotifications]
  )

  const {
    joinConversation,
    activeConversation,
    messages,
    userMap,
    messageMediaMap
  } = useChat(repairOrderId)

  const bottomRef = useRef<HTMLDivElement>(null)

  const triggeredInitialJoin = useRef<boolean>(false)

  useEffect(() => {
    if (!triggeredInitialJoin.current) {
      triggeredInitialJoin.current = true
      joinConversation(repairOrderId, true, false)
    }
  }, [joinConversation, repairOrderId])

  useEffect(() => {
    if (dialogOpen) {
      bottomRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'start'
      })
    }
  }, [dialogOpen, messages, messageMediaMap])

  useEffect(() => {
    if (dialogOpen && activeConversation) {
      activeConversation.setAllMessagesRead()
      if (unreadNotificationIds.length > 0) {
        markAsRead({
          authorization: accessToken,
          requestBody: unreadNotificationIds
        })
      }
    }
  }, [
    dialogOpen,
    activeConversation,
    messages,
    accessToken,
    markAsRead,
    unreadNotificationIds
  ])
  return (
    <Paper
      sx={{
        minWidth: dviDisplay || !overTabletBreakpoint ? undefined : 400,
        maxWidth: dviDisplay || !overTabletBreakpoint ? undefined : 400
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        p={1}
        sx={{ borderRadius: 1, background: '#CFECD5' }}>
        <Stack direction="row" alignItems="center">
          <BuildIcon fontSize="medium" />
          <Typography variant="h3">
            {`Repair Order #${repairOrderNumber} Chat${
              connectionStatus === 'connected' ? '' : ' (Disconnected)'
            }`}
          </Typography>
        </Stack>
        {!dviDisplay && (
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        )}
      </Stack>
      <ChatMessages
        userId={userId}
        messages={messages}
        userMap={userMap}
        messageMediaMap={messageMediaMap}
        activeConversation={activeConversation}
        disableDropzone={
          !(activeConversation && connectionStatus === 'connected')
        }
        bottomRef={bottomRef}
        minHeight={
          dviDisplay ? height - 270 : !overTabletBreakpoint ? height - 120 : 300
        }
        maxHeight={
          dviDisplay ? height - 270 : !overTabletBreakpoint ? height - 120 : 300
        }
        hideTemplates
      />
    </Paper>
  )
}
